
import { useState, useEffect } from 'react';

interface DashboardStats {
  totalCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  totalHours: number;
}

interface RecentActivity {
  id: string;
  type: 'course' | 'assignment' | 'achievement';
  title: string;
  description: string;
  time: string;
  icon: string;
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalCourses: 0,
    completedCourses: 0,
    inProgressCourses: 0,
    totalHours: 0
  });
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      
      // Fetch enrollments for current student
      const enrollmentsResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-enrollments?student_id=${userProfile.id}`, {
        method: 'GET',
        headers: {
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!enrollmentsResponse.ok) {
        const errorData = await enrollmentsResponse.json();
        throw new Error(errorData.error || 'Không thể tải dữ liệu khóa học');
      }

      const enrollments = await enrollmentsResponse.json();
      
      // Fetch assignments to calculate progress
      const assignmentsResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-student-assignments?student_id=${userProfile.id}`, {
        method: 'GET',
        headers: {
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });

      let coursesWithAssignments = [];
      let allAssignments: any[] = [];
      
      if (assignmentsResponse.ok) {
        const assignmentsData = await assignmentsResponse.json();
        coursesWithAssignments = assignmentsData.courses || [];
        
        // Flatten all assignments from all courses
        allAssignments = coursesWithAssignments.flatMap((course: any) => 
          (course.assignments || []).map((assignment: any) => ({
            ...assignment,
            course_id: course.id
          }))
        );
      }

      // Get unique courses (in case of duplicate enrollments)
      const uniqueCourses = Array.from(
        new Map(enrollments.map((e: any) => [e.course_id, e])).values()
      );
      
      // Calculate stats based on unique courses
      const totalCourses = uniqueCourses.length;
      
      // Calculate completed and in-progress courses based on assignments
      let completedCourses = 0;
      let inProgressCourses = 0;
      
      uniqueCourses.forEach((enrollment: any) => {
        const courseAssignments = allAssignments.filter((a: any) => a.course_id === enrollment.course_id);
        const totalAssignments = courseAssignments.length;
        
        if (totalAssignments === 0) {
          // No assignments yet, consider as in progress if enrolled
          inProgressCourses++;
        } else {
          const completedAssignments = courseAssignments.filter((a: any) => 
            a.status === 'graded' || a.status === 'submitted'
          ).length;
          
          const progress = (completedAssignments / totalAssignments) * 100;
          
          if (progress === 100) {
            completedCourses++;
          } else if (progress > 0) {
            inProgressCourses++;
          } else {
            inProgressCourses++;
          }
        }
      });

      // Calculate total hours (estimate based on enrollments and activities)
      const totalHours = Math.floor(enrollments.length * 2.5); // Estimate 2.5 hours per course

      setStats({
        totalCourses,
        completedCourses,
        inProgressCourses,
        totalHours
      });

      // Generate recent activities from assignments and enrollments
      const activities: RecentActivity[] = [];
      
      // Add recent submissions
      const recentSubmissions = allAssignments
        .filter((a: any) => a.status === 'submitted' || a.status === 'graded')
        .sort((a: any, b: any) => new Date(b.submitted_at || 0).getTime() - new Date(a.submitted_at || 0).getTime())
        .slice(0, 3);
      
      recentSubmissions.forEach((assignment: any) => {
        activities.push({
          id: `submission_${assignment.id}`,
          type: 'assignment',
          title: assignment.title,
          description: assignment.status === 'graded' 
            ? `Đã chấm điểm: ${assignment.score || 0}/${assignment.max_score}`
            : 'Đã nộp bài',
          time: new Date(assignment.submitted_at).toLocaleDateString('vi-VN'),
          icon: assignment.status === 'graded' ? 'ri-medal-line' : 'ri-file-text-line'
        });
      });

      // Add recent enrollments
      const recentEnrollments = enrollments
        .sort((a: any, b: any) => new Date(b.enrolled_at).getTime() - new Date(a.enrolled_at).getTime())
        .slice(0, 2);
      
      recentEnrollments.forEach((enrollment: any) => {
        activities.push({
          id: `enrollment_${enrollment.id}`,
          type: 'course',
          title: enrollment.course_title || 'Khóa học',
          description: 'Đã đăng ký khóa học',
          time: new Date(enrollment.enrolled_at).toLocaleDateString('vi-VN'),
          icon: 'ri-book-line'
        });
      });

      // Sort by time and limit to 5
      activities.sort((a, b) => {
        const dateA = new Date(a.time.split('/').reverse().join('-'));
        const dateB = new Date(b.time.split('/').reverse().join('-'));
        return dateB.getTime() - dateA.getTime();
      });

      setRecentActivities(activities.slice(0, 5));

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Không thể tải dữ liệu dashboard. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải dữ liệu...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <div className="flex items-center">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 sm:p-6 text-white">
        <h2 className="text-xl sm:text-2xl font-bold mb-2">Chào mừng trở lại!</h2>
        <p className="text-sm sm:text-base text-blue-100">Hãy tiếp tục hành trình học tập của bạn</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
              <i className="ri-book-line text-blue-600 text-lg sm:text-xl"></i>
            </div>
            <div className="sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Tổng khóa học</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalCourses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
              <i className="ri-check-line text-green-600 text-lg sm:text-xl"></i>
            </div>
            <div className="sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Đã hoàn thành</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.completedCourses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
              <i className="ri-play-line text-yellow-600 text-lg sm:text-xl"></i>
            </div>
            <div className="sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Đang học</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.inProgressCourses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
              <i className="ri-time-line text-purple-600 text-lg sm:text-xl"></i>
            </div>
            <div className="sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Tổng giờ học</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalHours}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Hoạt động gần đây</h3>
          <button
            onClick={fetchDashboardData}
            className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1"></i>
            Làm mới
          </button>
        </div>

        {recentActivities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 sm:py-8 text-gray-500">
            <i className="ri-history-line text-2xl sm:text-3xl mb-2"></i>
            <p className="text-xs sm:text-sm">Chưa có hoạt động nào</p>
            <p className="text-xs mt-1">Hãy bắt đầu học một khóa học mới!</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${activity.icon} text-blue-600 text-sm sm:text-base`}></i>
                </div>
                <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-900 truncate">{activity.title}</h4>
                  <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                </div>
                <div className="text-xs text-gray-400 ml-2 whitespace-nowrap">{activity.time}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
